import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { useUser } from '@client/hooks/use-user.hooks';
import { isAdmin } from '@shared/helpers/user.helpers';
import { humanizeJsonSchema } from '../AdminAIChatTools/humanize-schema.helpers';
import {
  FFU_PIPE_ID,
  PROJECT_FILE_PIPES,
  type ProjectFilePipeId,
} from '@shared/models/project-file-pipe.model';
import {
  countJsonSchemaProperties,
  defaultPipeFlowNodeId,
  getProjectFilePipeFlowGraph,
  resolvePipeFlowNode,
  type PipelineFlowEdgeKind,
  type PipelineFlowKind,
  type PipelineFlowLane,
  type PipelineFlowNode,
  type PipelineFlowSchemaRef,
} from './pipeline-flow.helpers';

type OpenPipe = (id: ProjectFilePipeId) => void;

const COL_W = 220;
const ROW_H = 188;
const NODE_W = 196;
const NODE_H = 132;
const PAD = 44;
const LANE_INSET = 10;
const LANE_LABEL_H = 26;

type SchemaView = 'readable' | 'json';

const nodeBox = (node: PipelineFlowNode) => ({
  x: PAD + node.col * COL_W,
  y: PAD + node.row * ROW_H,
  w: NODE_W,
  h: NODE_H,
});

const laneBox = (lane: PipelineFlowLane) => ({
  left: PAD + lane.col * COL_W - LANE_INSET,
  top: PAD + lane.row * ROW_H - LANE_INSET - LANE_LABEL_H,
  width: (lane.cols - 1) * COL_W + NODE_W + LANE_INSET * 2,
  height: (lane.rows - 1) * ROW_H + NODE_H + LANE_INSET * 2 + LANE_LABEL_H,
});

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
  },
  body: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  graphPane: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  graphHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2, 3, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  graphScroll: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(2),
  },
  graph: {
    position: 'relative',
  },
  edges: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
  },
  edge: {
    fill: 'none',
    stroke: theme.palette.divider,
    strokeWidth: 1.5,
  },
  edgeNext: {
    strokeDasharray: 'none',
  },
  edgeActive: {
    stroke: theme.palette.primary.main,
    strokeWidth: 2,
  },
  edgeSkip: {
    strokeDasharray: '5 4',
  },
  edgeLabel: {
    fill: theme.palette.text.secondary,
    fontSize: 11,
  },
  lane: {
    position: 'absolute',
    padding: theme.spacing(0.5, 1),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
  node: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    margin: 0,
    padding: theme.spacing(1.25, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    textAlign: 'left',
    cursor: 'pointer',
  },
  nodeConditional: {
    borderStyle: 'dashed',
  },
  nodeSelected: {
    borderColor: theme.palette.primary.main,
  },
  nodeTitle: {
    fontWeight: 600,
    fontSize: '0.875rem',
    lineHeight: 1.25,
  },
  nodeMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  inspector: {
    display: 'flex',
    flexDirection: 'column',
    width: 460,
    minWidth: 360,
    overflow: 'auto',
    padding: theme.spacing(2, 2.5, 4),
    borderLeft: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  inspectorHead: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  inspectorCondition: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
  },
  inspectorOutputs: {
    marginTop: theme.spacing(3),
  },
  inspectorChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  portList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  port: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  schemaBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  schemaToolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  schemaMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  schemaToggle: {
    alignSelf: 'flex-start',
    margin: 0,
    padding: 0,
    border: 'none',
    background: 'none',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    textAlign: 'left',
    font: 'inherit',
  },
  schemaPre: {
    margin: 0,
    padding: theme.spacing(1.5),
    overflow: 'auto',
    maxHeight: 420,
    backgroundColor: theme.palette.action.hover,
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  typeChipsWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1),
  },
  typeChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  denied: {
    padding: theme.spacing(3),
  },
}));

const KIND_LABEL: Record<PipelineFlowKind, string> = {
  ai: 'AI call',
  code: 'Code',
  gate: 'Gate',
  io: 'I/O',
};

const EDGE_KIND_CLASS: Record<PipelineFlowEdgeKind, 'edgeNext' | 'edgeSkip'> = {
  next: 'edgeNext',
  skip: 'edgeSkip',
};

const renderReadableSchema = (text: string): React.ReactNode =>
  text.split('\n').map((line, i, arr) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/);
    return (
      <React.Fragment key={i}>
        {parts.map((part, j) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={j}>{part.slice(2, -2)}</strong>;
          }
          return part;
        })}
        {i < arr.length - 1 && '\n'}
      </React.Fragment>
    );
  });

const SchemaSlice: React.FC<{
  item: PipelineFlowSchemaRef;
  open: boolean;
  onToggle: () => void;
}> = ({ item, open, onToggle }) => {
  const { classes } = useStyles();
  const [view, setView] = useState<SchemaView>('readable');
  const propCount = countJsonSchemaProperties(item.schema);
  const text =
    view === 'readable'
      ? humanizeJsonSchema(item.schema)
      : JSON.stringify(item.schema, null, 2);

  return (
    <Box className={classes.schemaBlock}>
      <Box className={classes.schemaToolbar}>
        <Box className={classes.schemaMeta}>
          <Typography variant="body2">{item.label}</Typography>
          {item.structuredOutputName != null && (
            <Chip
              size="small"
              variant="outlined"
              label={item.structuredOutputName}
            />
          )}
          {propCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              {propCount} root keys
            </Typography>
          )}
        </Box>
        <button
          type="button"
          className={classes.schemaToggle}
          onClick={onToggle}
          aria-expanded={open}
        >
          {open ? 'Hide schema' : 'Show schema'}
        </button>
      </Box>
      {open && (
        <>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={view}
            onChange={(_, next: SchemaView | null) => next && setView(next)}
            aria-label={`Schema view for ${item.id}`}
          >
            <ToggleButton value="readable">Readable</ToggleButton>
            <ToggleButton value="json">JSON</ToggleButton>
          </ToggleButtonGroup>
          <Typography component="pre" className={classes.schemaPre}>
            {view === 'readable' ? renderReadableSchema(text) : text}
          </Typography>
        </>
      )}
    </Box>
  );
};

const PortBlock: React.FC<{
  heading: string;
  ports: PipelineFlowNode['inputs'];
  openSchemaIds: ReadonlySet<string>;
  onToggleSchema: (id: string) => void;
}> = ({ heading, ports, openSchemaIds, onToggleSchema }) => {
  const { classes } = useStyles();
  return (
    <Box>
      <Typography variant="subtitle2">{heading}</Typography>
      <Box className={classes.portList}>
        {ports.map((port) => (
          <Box key={port.id} className={classes.port}>
            <Typography variant="body2">{port.label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {port.summary}
            </Typography>
            {port.schemas?.map((schema) => (
              <SchemaSlice
                key={schema.id}
                item={schema}
                open={openSchemaIds.has(schema.id)}
                onToggle={() => onToggleSchema(schema.id)}
              />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const AdminProjectFilePipelineFlow: React.FC = () => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const user = useUser();
  const callerIsAdmin = useMemo(() => isAdmin(user), [user]);
  const [pipeId, setPipeId] = useState<ProjectFilePipeId>(FFU_PIPE_ID);
  const graph = useMemo(() => getProjectFilePipeFlowGraph(pipeId), [pipeId]);
  const [selectedId, setSelectedId] = useState<string>(() =>
    defaultPipeFlowNodeId(getProjectFilePipeFlowGraph(FFU_PIPE_ID)),
  );
  const [openSchemaIds, setOpenSchemaIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const selected = resolvePipeFlowNode(graph, selectedId);
  const activeId = selected?.id ?? '';
  const extractNode = graph.nodes.find((node) => node.id === 'extract');
  const extractSchemas = extractNode?.outputs.find(
    (p) => p.id === 'extract-ai',
  )?.schemas;

  const cols = Math.max(1, ...graph.nodes.map((node) => node.col + 1));
  const rows = Math.max(1, ...graph.nodes.map((node) => node.row + 1));
  const width = PAD * 2 + cols * COL_W;
  const height = PAD * 2 + rows * ROW_H;
  const nodeById = useMemo(
    () => new Map(graph.nodes.map((n) => [n.id, n])),
    [graph],
  );

  const openPipe = useCallback<OpenPipe>((id) => {
    const next = getProjectFilePipeFlowGraph(id);
    setPipeId(id);
    setSelectedId(defaultPipeFlowNodeId(next));
    setOpenSchemaIds(new Set());
  }, []);

  const selectNode = (id: string, schemaId?: string) => {
    setSelectedId(id);
    setOpenSchemaIds(schemaId == null ? new Set() : new Set([schemaId]));
  };

  const toggleSchema = (id: string) => {
    setOpenSchemaIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!callerIsAdmin) {
    return (
      <Box className={classes.denied}>
        <Typography color="error">
          This page is only available to administrators.
        </Typography>
      </Box>
    );
  }

  if (selected == null) return null;

  return (
    <Box className={classes.root}>
      <Box className={classes.body}>
        <Box className={classes.graphPane}>
          <Box className={classes.graphHeader}>
            <Typography variant="h6">
              {PROJECT_FILE_PIPES.find((pipe) => pipe.id === pipeId)?.label ??
                pipeId}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pipeId}
            </Typography>
            <Box className={classes.legend}>
              {PROJECT_FILE_PIPES.map((pipe) => (
                <Chip
                  key={pipe.id}
                  size="small"
                  label={pipe.label}
                  variant={pipe.id === pipeId ? 'filled' : 'outlined'}
                  onClick={() => openPipe(pipe.id)}
                />
              ))}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Click a step. Inspector shows inputs and outputs. Large schemas
              stay collapsed until opened. Shaded boxes group steps that share a
              concurrency scope; stacked steps in one column run in parallel. A
              dashed step runs only if its condition holds; a dashed arrow is
              the path when it is skipped.
            </Typography>
            <Box className={classes.legend}>
              {(Object.entries(KIND_LABEL) as [PipelineFlowKind, string][]).map(
                ([kind, label]) => (
                  <Chip
                    key={kind}
                    size="small"
                    variant={kind === 'ai' ? 'filled' : 'outlined'}
                    label={label}
                  />
                ),
              )}
            </Box>
          </Box>
          <Box className={classes.graphScroll}>
            <Box className={classes.graph} style={{ width, height }}>
              {graph.lanes.map((lane) => (
                <Box
                  key={lane.id}
                  className={classes.lane}
                  style={laneBox(lane)}
                >
                  <Typography variant="caption" color="text.secondary">
                    {lane.label}
                  </Typography>
                </Box>
              ))}
              <svg
                className={classes.edges}
                width={width}
                height={height}
                aria-hidden
              >
                <defs>
                  <marker
                    id="pipeline-arrow"
                    viewBox="0 0 10 10"
                    refX="8"
                    refY="5"
                    markerWidth="7"
                    markerHeight="7"
                    orient="auto-start-reverse"
                  >
                    <path
                      d="M 0 0 L 10 5 L 0 10 z"
                      fill={theme.palette.text.secondary}
                    />
                  </marker>
                </defs>
                {graph.edges.map((edge) => {
                  const from = nodeById.get(edge.from);
                  const to = nodeById.get(edge.to);
                  if (from == null || to == null) return null;
                  const a = nodeBox(from);
                  const b = nodeBox(to);
                  const sx = a.x + a.w;
                  const sy = a.y + a.h / 2;
                  const tx = b.x;
                  const ty = b.y + b.h / 2;
                  const dx = Math.max(40, (tx - sx) / 2);
                  const active = edge.from === activeId || edge.to === activeId;
                  const mx = (sx + tx) / 2;
                  const my = (sy + ty) / 2;
                  return (
                    <g key={`${edge.from}-${edge.to}`}>
                      <path
                        d={`M ${sx} ${sy} C ${sx + dx} ${sy}, ${tx - dx} ${ty}, ${tx} ${ty}`}
                        className={cx(
                          classes.edge,
                          classes[EDGE_KIND_CLASS[edge.kind]],
                          active && classes.edgeActive,
                        )}
                        markerEnd="url(#pipeline-arrow)"
                      />
                      {edge.label != null && (
                        <text
                          x={mx}
                          y={my - 6}
                          textAnchor="middle"
                          className={classes.edgeLabel}
                        >
                          {edge.label}
                        </text>
                      )}
                    </g>
                  );
                })}
              </svg>
              {graph.nodes.map((node) => {
                const box = nodeBox(node);
                const selectedNode = node.id === activeId;
                return (
                  <Box
                    key={node.id}
                    component="button"
                    type="button"
                    className={cx(
                      classes.node,
                      node.condition != null && classes.nodeConditional,
                      selectedNode && classes.nodeSelected,
                    )}
                    style={{
                      left: box.x,
                      top: box.y,
                      width: box.w,
                      height: box.h,
                    }}
                    onClick={() => selectNode(node.id)}
                    aria-pressed={selectedNode}
                  >
                    <Typography component="span" className={classes.nodeTitle}>
                      {node.shortTitle}
                    </Typography>
                    <Box className={classes.nodeMeta}>
                      <Chip size="small" label={KIND_LABEL[node.kind]} />
                      {node.kind === 'ai' && node.defaultModel != null && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={node.defaultModel}
                        />
                      )}
                      {node.fanOut != null && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={node.fanOut}
                        />
                      )}
                      {node.condition != null && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="warning"
                          label={node.condition.label}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {extractSchemas != null && extractSchemas.length > 0 && (
              <Box className={classes.typeChipsWrap}>
                <Typography variant="caption" color="text.secondary">
                  Extract types — opens that output schema
                </Typography>
                <Box className={classes.typeChips}>
                  {extractSchemas.map((schema) => (
                    <Chip
                      key={schema.id}
                      size="small"
                      variant={
                        openSchemaIds.has(schema.id) ? 'filled' : 'outlined'
                      }
                      label={schema.label}
                      onClick={() => selectNode('extract', schema.id)}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Box className={classes.inspector} component="aside">
          <Box className={classes.inspectorHead}>
            <Typography variant="caption" color="text.secondary">
              {selected.phaseLabel}
            </Typography>
            <Typography variant="h6">{selected.title}</Typography>
            <Box className={classes.inspectorChips}>
              <Chip size="small" label={KIND_LABEL[selected.kind]} />
              {selected.useCaseLabel != null && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={selected.useCaseLabel}
                />
              )}
              {selected.defaultModel != null && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={selected.defaultModel}
                />
              )}
              {selected.fanOut != null && (
                <Chip size="small" variant="outlined" label={selected.fanOut} />
              )}
            </Box>
            {selected.condition != null && (
              <Box className={classes.inspectorCondition}>
                <Chip
                  size="small"
                  variant="outlined"
                  color="warning"
                  label={selected.condition.label}
                />
                <Typography variant="body2" color="text.secondary">
                  {selected.condition.detail}
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="text.secondary">
              {selected.purpose}
            </Typography>
            {selected.parallelism != null && (
              <Typography variant="body2" color="text.secondary">
                {selected.parallelism}
              </Typography>
            )}
          </Box>
          <PortBlock
            heading="Inputs"
            ports={selected.inputs}
            openSchemaIds={openSchemaIds}
            onToggleSchema={toggleSchema}
          />
          <Box className={classes.inspectorOutputs}>
            <PortBlock
              heading="Outputs"
              ports={selected.outputs}
              openSchemaIds={openSchemaIds}
              onToggleSchema={toggleSchema}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
